import { useTranslation } from "react-i18next";
import defaultContent from "../../../../content/earningsCalculator";
import LeafLinkButton from "../../../../common/leaf/LeafLinkButton/LeafLinkButton";
import { IconResetCircle } from "../../../../common/icons/IconResetCircle/IconResetCircle";
import "./EarningsCalculatorABOLeg.scss";
import LeafSlider, { MarkLabels } from "../../../../common/leaf/LeafSlider/LeafSlider";
import { ABOLegBody, removeABOLeg, updateABOLegs } from "../../../../reducers/earningCalculator";
import { useAppDispatch } from "../../../../store";
import LeafInput from "../../../../common/leaf/LeafInput/LeafInput";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import ABONameComponent from "../../../../components/ABOProfile/ABOName/ABOName";
import { removeABOLegTrack, resetGPVTrack } from "../../../../common/tealium/EarningCalculator/EarningCalculatorTrack";
import { storedLocale } from "../../../../components/Authentication/util";

type Mark = {
  Label: string;
  Value: number;
};

type Props = {
  IsLoading: boolean;
  ABOLeg: ABOLegBody;
  CountryCode: string;
  Marks: Mark[];
  Labels: MarkLabels[];
  Min: number;
  Max: number;
  LegCount?: number;
  disableInput?: boolean;
};

const EarningsCalculatorABOLeg = (props: Props) => {
  const { t } = useTranslation(["earningsCalculator"]);
  const dispatch = useAppDispatch();
  const locale = storedLocale();

  const { IsLoading, ABOLeg, CountryCode, Marks, Labels, Min, Max, LegCount = 0, disableInput = false } = props;
  const { AffABO, Name, Removeable, GroupPV, GroupPVBase = 0 } = ABOLeg;
  const { ABOName, ABOLocalName, ISOLanguage, PrivacyFlag } = Name;
  const isDisabled = GroupPVBase === Max || disableInput;

  const handleRemoveLeg = () => {
    dispatch(removeABOLeg(ABOLeg));
    removeABOLegTrack();
  };

  const handleValueChange = (newValue: number | "") => {
    if (Number(newValue) <= GroupPVBase) {
      dispatch(updateABOLegs({ ...ABOLeg, GroupPV: GroupPVBase }));
    }
    dispatch(updateABOLegs({ ...ABOLeg, GroupPV: Number(newValue) }));
  };

  const handleReset = () => {
    if (isDisabled) return;
    resetGPVTrack();
    dispatch(updateABOLegs({ ...ABOLeg, GroupPV: GroupPVBase }));
  };

  return (
    <div className={`earnings-calculator-abo-leg${Removeable ? "__added-card" : ""}`}>
      <div className="earnings-calculator-abo-leg__header">
        <div className="earnings-calculator-abo-leg__title">
          <LeafRectangleLoader isLoading={IsLoading} height="1.5rem" width="5rem">
            {Removeable ? (
              <>
                {`${t("aboLegs.legTile.header", defaultContent["aboLegs"]["legTile"]["header"], {
                  legCount: LegCount + 1,
                })}`}
                <LeafLinkButton
                  onClick={handleRemoveLeg}
                  label={`${t("aboLegs.legTile.remove", defaultContent["aboLegs"]["legTile"]["remove"])}`}
                  as={"link"}
                  theme="blue"
                  fontWeight="bold"
                  fontSize="normal"
                >{`${t("aboLegs.legTile.remove", defaultContent["aboLegs"]["legTile"]["remove"])}`}</LeafLinkButton>
              </>
            ) : (
              <>
                <div className="earnings-calculator-abo-leg__title__abo-name">
                  {
                    <ABONameComponent
                      name={ABOName}
                      localName={ABOLocalName}
                      privacyFlag={PrivacyFlag}
                      languageCode={ISOLanguage}
                      locale={locale}
                    />
                  }
                </div>
                <div className="earnings-calculator-abo-leg__title__abo-no">{`#${AffABO}`}</div>
              </>
            )}
          </LeafRectangleLoader>
        </div>
        <div className="earnings-calculator-abo-leg__input">
          <LeafInput
            isLoading={IsLoading}
            countryCode={CountryCode}
            value={GroupPV}
            placeholder={`${t("aboLegs.legTile.inputGpvValue", defaultContent["aboLegs"]["legTile"]["inputGpvValue"])}`}
            onChange={handleValueChange}
            suffix={`${t("aboLegs.legTile.gpv", defaultContent["aboLegs"]["legTile"]["gpv"])}`}
            min={GroupPVBase}
            max={Max}
            disabled={isDisabled}
          />
          <LeafLinkButton
            onClick={handleReset}
            label={`${t("progressTile.resetValues", defaultContent["progressTile"]["resetValues"])}`}
            as={"link"}
          >
            <IconResetCircle disabled={isDisabled} />
          </LeafLinkButton>
        </div>
      </div>
      <div className="earnings-calculator-abo-leg__content">
        <LeafSlider
          isLoading={IsLoading}
          base={GroupPVBase}
          value={GroupPV}
          min={Min}
          max={Max}
          marks={Marks}
          onChange={handleValueChange}
          tooltipSuffixText={`${t("aboLegs.legTile.gpv", defaultContent["aboLegs"]["legTile"]["gpv"])}`}
          markLabels={Labels}
          disabled={isDisabled}
          ariaLabel={`${t("aboLegs.legTile.header", defaultContent["aboLegs"]["legTile"]["header"], { legCount: LegCount + 1 })} ${t("aboLegs.legTile.gpv", defaultContent["aboLegs"]["legTile"]["gpv"])}`}
        />
      </div>
    </div>
  );
};

export default EarningsCalculatorABOLeg;
